/**
 * Payments, as the patient app calls them (MASTER_PLAN M7.7, API.md §2.3 and §2.10,
 * FR-PAY-01, FR-PAY-02, FR-PAY-08, ADR-010).
 *
 * Six routes, one client, and the same shared transport as `booking.ts` — so every call
 * carries `credentials: 'include'` and therefore the `ha_draft` cookie (the funnel) or
 * the patient session cookie (the portal). Which cookie matters is the server's business;
 * this file never names one.
 *
 * | Method | Route | Identity |
 * |---|---|---|
 * | `config` | `GET /public/payments/config` | none — public, and the answer is per clinic |
 * | `saveDraftCard` | `POST /public/drafts/current/payment-method` | `ha_draft` |
 * | `saveBookingCard` | `POST /public/bookings/:id/payment-method` | `ha_draft` |
 * | `getCardOnFile` | `GET /patient/payment-method` | patient session |
 * | `saveCardOnFile` | `POST /patient/payment-method` | patient session |
 * | `removeCardOnFile` | `DELETE /patient/payment-method` | patient session |
 *
 * ## What never passes through here
 *
 * A PAN, a CVV and a vault token. The browser holds a **nonce** for the length of one
 * call — the hosted iframe made it, it is single use, it lives fifteen minutes — and this
 * module exchanges it for a `PaymentMethodSummary` (brand, last four, expiry) that is
 * safe to render. The nonce is never logged, never put in state and never written to
 * storage (SECURITY.md §8, `packages/contracts/src/payments.ts`).
 *
 * `amountCents` is likewise absent: the request schema is `.strict()` and the patient's
 * browser cannot say what a card is for (FR-PAY-07).
 *
 * ## Why the bodies are typed from `@platform/contracts`
 *
 * The same reason `booking.ts` gives: these controllers take `@Body() body: unknown` and
 * validate with zod, so Nest's Swagger plugin emits `requestBody?: never` and `client.post`
 * would refuse a body it will happily send. Naming the shape from the schema the server
 * parses with makes a drift a compile error. Responses keep the generated types.
 */

import { isApiError, type ApiClient, type ApiError } from '@platform/api-client';
import type {
  PaymentConfig,
  PaymentMethodSummary,
  SavePaymentMethodRequest,
} from '@platform/contracts';

import { apiClient } from './client';

/* ------------------------------------------------------------------ *
 * Shapes
 * ------------------------------------------------------------------ */

/**
 * The body of all three save routes (`SavePaymentMethodRequestSchema`).
 *
 * `consent` is sent when the patient ticked the financial agreement, and omitted when
 * they did not: an absent consent records nothing, which is the honest answer, whereas a
 * `false` would be a consent row saying the patient was asked and refused — a claim this
 * client is in no position to make.
 */
export type SaveCardInput = SavePaymentMethodRequest;

/**
 * Which of the three save routes a card is going to.
 *
 * `custom` is the portal's: its screens take a `PortalClient` from a context so a test
 * can hand them one, and the card routes are served through that same object
 * (`portal-client-context.tsx`). Passing the call in keeps the nonce inside
 * `useCardForm` — the caller supplies a destination, never a place to read the token.
 */
export type SaveCardTarget =
  | { kind: 'draft' }
  | { kind: 'booking'; bookingId: string }
  | { kind: 'portal' }
  | {
      kind: 'custom';
      save(input: SaveCardInput, idempotencyKey: string): Promise<PaymentMethodSummary>;
    };

/**
 * The financial agreement version recorded when the clinic has published none of its own.
 *
 * It is printed on `/payment` under the two tick boxes, which is what makes it honest: a
 * `consents` row whose `document_version` names a text nobody can produce is not a
 * consent anybody can defend two years later (`packages/contracts/src/tenant.ts`).
 * Bump it whenever that wording changes.
 */
export const PAYMENT_AGREEMENT_VERSION = 'v1.0';

export interface PaymentsClient {
  /**
   * The clinic's tokenization config. Re-read on every mount: the server caches it for
   * a minute (`Cache-Control: private, max-age=60`), which is the width of the window in
   * which a rotated key is still the old one (INTEGRATIONS.md §10.1).
   */
  config(signal?: AbortSignal): Promise<PaymentConfig>;

  /** Vault a card against the draft this browser owns. `Idempotency-Key` is required. */
  saveDraftCard(input: SaveCardInput, idempotencyKey: string): Promise<PaymentMethodSummary>;

  /** The same, named by draft id **or** appointment id — "add a card" after confirm. */
  saveBookingCard(
    bookingId: string,
    input: SaveCardInput,
    idempotencyKey: string,
  ): Promise<PaymentMethodSummary>;

  /** The signed-in patient's card. `null` before they have saved one. */
  getCardOnFile(signal?: AbortSignal): Promise<PaymentMethodSummary | null>;

  /** Replace it. The old one is marked `removed` server-side, never deleted. */
  saveCardOnFile(input: SaveCardInput, idempotencyKey: string): Promise<PaymentMethodSummary>;

  /** Take it off file. `204` whether or not there was one. */
  removeCardOnFile(): Promise<void>;
}

const PUBLIC = '/api/v1/public';
const PATIENT = '/api/v1/patient';

export function createPaymentsClient(client: ApiClient): PaymentsClient {
  const withSignal = (signal: AbortSignal | undefined): { signal?: AbortSignal } =>
    signal === undefined ? {} : { signal };

  return {
    config: async (signal) => {
      const body = await client.get('/api/v1/public/payments/config', withSignal(signal));
      return body.data;
    },

    saveDraftCard: async (input, idempotencyKey) => {
      const body = await client.request<{ data: { paymentMethod: PaymentMethodSummary } }>(
        'post',
        `${PUBLIC}/drafts/current/payment-method`,
        input,
        { idempotencyKey },
      );
      return body.data.paymentMethod;
    },

    saveBookingCard: async (bookingId, input, idempotencyKey) => {
      const body = await client.request<{ data: { paymentMethod: PaymentMethodSummary } }>(
        'post',
        `${PUBLIC}/bookings/${encodeURIComponent(bookingId)}/payment-method`,
        input,
        { idempotencyKey },
      );
      return body.data.paymentMethod;
    },

    getCardOnFile: async (signal) => {
      const body = await client.get('/api/v1/patient/payment-method', withSignal(signal));
      return body.data.paymentMethod;
    },

    saveCardOnFile: async (input, idempotencyKey) => {
      const body = await client.request<{ data: { paymentMethod: PaymentMethodSummary } }>(
        'post',
        `${PATIENT}/payment-method`,
        input,
        { idempotencyKey },
      );
      return body.data.paymentMethod;
    },

    removeCardOnFile: async () => {
      await client.delete('/api/v1/patient/payment-method');
    },
  };
}

/** The app's own instance, over the single client and query cache. */
export const paymentsClient: PaymentsClient = createPaymentsClient(apiClient);

/* ------------------------------------------------------------------ *
 * Failures
 * ------------------------------------------------------------------ */

/**
 * What the app does about a refused save, and the sentence the patient reads.
 *
 * Every message here is **ours**. The processor's own text is never shown: a decline
 * reason written for a merchant's back office ("Do not honour", "AVS N") tells a patient
 * nothing they can act on and is not a string any vendor promises to keep (rules §23.8,
 * §35.4, SECURITY.md §8).
 */
export type SaveCardAction =
  /** The nonce was refused. Re-tokenize: a nonce is single use, so the fields must be re-entered. */
  | 'retokenize'
  /** The payment step was reached before the details step. Send the patient back. */
  | 'details'
  /** The clinic takes no cards. Skip the step; the booking continues without one. */
  | 'skip'
  /** The draft or booking is gone. Start the funnel again. */
  | 'restart'
  /** The first request with this key is still running. Wait a beat and repeat it. */
  | 'wait'
  /** Same key, different body. Mint a new key and repeat it. */
  | 'newKey'
  /** Too many attempts. Back off. */
  | 'backoff'
  /** The processor did not answer. Worth trying again. */
  | 'retry'
  /** Anything else. */
  | 'failed';

export interface SaveCardFailure {
  action: SaveCardAction;
  /** Shown to the patient. Never a vendor string. */
  message: string;
  /** Seconds, when the API sent a `Retry-After`. */
  retryAfter?: number;
}

/** The copy, in one place so a sentence is changed once (rules §35.2). */
export const CARD_ERRORS = {
  retokenize:
    "We couldn't save that card. Please check the number, expiry and security code and try again.",
  details: 'We need your details before we can save a card.',
  skip: 'This clinic is not taking cards online at the moment.',
  restart: 'We could not find your booking. Please start again.',
  wait: 'We are still saving your card. One moment.',
  backoff: 'Too many attempts. Please wait a moment and try again.',
  retry: 'Our payment provider did not answer. Please try again.',
  failed: 'We could not save your card. Please try again, or call the clinic.',
  /** `DELETE /patient/payment-method` while a charge is still pointing at the card. */
  liveIntent:
    'We cannot remove this card yet — the clinic has a charge in progress against it. ' +
    'Please call the clinic.',
} as const;

/**
 * Turn an `ApiError` into what to do about it (API.md §1.6).
 *
 * The two `VALIDATION_FAILED` cases are told apart by the issue path, which is what the
 * controller writes: `['nonce']` is the processor refusing the card, `['details']` is a
 * client reaching the payment step before the details step.
 */
export function classifySaveCardError(error: unknown): SaveCardFailure {
  if (!isApiError(error)) {
    return { action: 'failed', message: CARD_ERRORS.failed };
  }

  const failure = ((): SaveCardFailure => {
    switch (error.code) {
      case 'VALIDATION_FAILED':
        return issuePathIncludes(error, 'details')
          ? { action: 'details', message: CARD_ERRORS.details }
          : { action: 'retokenize', message: CARD_ERRORS.retokenize };
      case 'BAD_REQUEST':
        return { action: 'retokenize', message: CARD_ERRORS.retokenize };
      case 'FEATURE_DISABLED':
        return { action: 'skip', message: CARD_ERRORS.skip };
      case 'NOT_FOUND':
      case 'TENANT_NOT_FOUND':
      case 'DRAFT_EXPIRED':
        return { action: 'restart', message: CARD_ERRORS.restart };
      case 'IDEMPOTENCY_IN_PROGRESS':
        return { action: 'wait', message: CARD_ERRORS.wait };
      case 'IDEMPOTENCY_MISMATCH':
        return { action: 'newKey', message: CARD_ERRORS.failed };
      case 'RATE_LIMITED':
        return { action: 'backoff', message: CARD_ERRORS.backoff };
      case 'INTEGRATION_UNAVAILABLE':
      case 'INTEGRATION_TIMEOUT':
      case 'INTEGRATION_ERROR':
      case 'NETWORK_ERROR':
        return { action: 'retry', message: CARD_ERRORS.retry };
      default:
        return { action: 'failed', message: CARD_ERRORS.failed };
    }
  })();

  return error.retryAfter === undefined ? failure : { ...failure, retryAfter: error.retryAfter };
}

/** `422 INVALID_STATE_TRANSITION` — a live charge intent still points at the card. */
export function isLiveChargeConflict(error: unknown): error is ApiError {
  return isApiError(error) && error.code === 'INVALID_STATE_TRANSITION';
}

function issuePathIncludes(error: ApiError, segment: string): boolean {
  return error.issues.some((issue) => issue.path.some((part) => String(part) === segment));
}

/* ------------------------------------------------------------------ *
 * Idempotency
 * ------------------------------------------------------------------ */

/**
 * A fresh key per **nonce** (API.md §1.8, FR-PAY-01).
 *
 * A nonce is single use, so "the same request" means "the same nonce": replaying a key
 * with a new nonce is a different request and would answer `422 IDEMPOTENCY_MISMATCH`.
 * The key is minted once per tokenization and reused for the retries of that one save,
 * which is what makes a double-tapped button one vaulted card.
 *
 * `newIdempotencyKey` itself lives in `booking.ts`; re-exported rather than re-written so
 * there is one uuid v4 in the app (rules §60.5).
 */
export { newIdempotencyKey } from './booking';
